/** Sidebar, top bar and the permission-aware menu (§7). */
import { useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Banknote,
  BookOpen,
  BarChart3,
  Building2,
  ChevronDown,
  FileSpreadsheet,
  FolderKanban,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Download,
  RefreshCw,
  Receipt,
  ScrollText,
  Settings,
  ShoppingCart,
  Truck,
  Sun,
  Target,
  User,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { applyUpdate, useInstallPrompt, useUpdateReady } from '../lib/pwa';
import { useTheme } from '../lib/theme';
import { useSidebarCollapsed } from '../lib/sidebar';
import { Button } from '../components/ui';

interface NavItem {
  to: string;
  label: string;
  Icon: LucideIcon;
  /** Any one of these is enough to see the item. */
  permissions?: string[];
  children?: NavItem[];
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', Icon: LayoutDashboard, permissions: ['dashboard.view'] },
  { to: '/customers', label: 'Customers', Icon: Building2, permissions: ['customers.view'] },
  { to: '/leads', label: 'Leads', Icon: Target, permissions: ['leads.view_own', 'leads.view_all'] },
  {
    to: '/sales-orders',
    label: 'Sales Orders',
    Icon: Receipt,
    permissions: ['salesorders.view'],
    children: [
      { to: '/billing', label: 'Billing', Icon: Banknote, permissions: ['billing.view'] },
      {
        to: '/sales-orders/quotations',
        label: 'Quotations',
        Icon: ScrollText,
        permissions: ['suppliers.view'],
      },
      {
        to: '/sales-orders/purchase-orders',
        label: 'Purchase Orders',
        Icon: ShoppingCart,
        permissions: ['po.view'],
      },
    ],
  },
  {
    to: '/projects',
    label: 'Projects',
    Icon: FolderKanban,
    permissions: ['projects.view_all', 'projects.view_assigned'],
  },
  {
    to: '/suppliers',
    label: 'Suppliers',
    Icon: Truck,
    permissions: ['suppliers.view'],
    children: [
      { to: '/suppliers', label: 'Suppliers', Icon: Truck, permissions: ['suppliers.view'] },
      { to: '/suppliers/price-book', label: 'Price Book', Icon: BookOpen, permissions: ['pricebook.view'] },
    ],
  },
  {
    to: '/payables',
    label: 'Accounts Payable',
    Icon: FileSpreadsheet,
    permissions: ['ap.view_all', 'ap.view_own', 'ap.create'],
  },
  {
    to: '/reports/ar-aging',
    label: 'Reports',
    Icon: BarChart3,
    permissions: ['reports.ar', 'reports.ap', 'reports.profitability'],
    children: [
      { to: '/reports/ar-aging', label: 'AR Aging', Icon: BarChart3, permissions: ['reports.ar'] },
      { to: '/reports/ap', label: 'Accounts Payable', Icon: BarChart3, permissions: ['reports.ap'] },
      {
        to: '/reports/profitability',
        label: 'Profitability',
        Icon: BarChart3,
        permissions: ['reports.profitability'],
      },
    ],
  },
  {
    to: '/management/users',
    label: 'Management',
    Icon: Settings,
    permissions: ['users.manage', 'roles.manage', 'parameters.manage', 'settings.manage', 'audit.view'],
    children: [
      { to: '/management/users', label: 'Users', Icon: User, permissions: ['users.manage'] },
      { to: '/management/api-keys', label: 'API Keys', Icon: KeyRound, permissions: ['users.manage'] },
      { to: '/management/roles', label: 'Roles & Permissions', Icon: Settings, permissions: ['roles.manage'] },
      { to: '/management/ap-accounts', label: 'AP Accounts', Icon: Banknote, permissions: ['parameters.manage'] },
      { to: '/management/parameters', label: 'Parameters', Icon: Settings, permissions: ['parameters.manage'] },
      { to: '/management/custom-fields', label: 'Customer Fields', Icon: Settings, permissions: ['parameters.manage'] },
      { to: '/management/tax', label: 'Tax & Finance', Icon: Settings, permissions: ['settings.manage'] },
      { to: '/management/company', label: 'Company Profile', Icon: Building2, permissions: ['settings.manage'] },
      { to: '/management/audit', label: 'Audit Log', Icon: FileSpreadsheet, permissions: ['audit.view'] },
    ],
  },
];

function NavTree({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  /** Icons only. Submenus are hidden: a parent's own link is its landing page. */
  collapsed?: boolean;
}): JSX.Element {
  const { can } = useAuth();
  const [open, setOpen] = useState<string | null>('/management/users');

  const visible = (item: NavItem): boolean => !item.permissions || can(...item.permissions);

  return (
    <ul className="space-y-1">
      {NAV.filter(visible).map((item) => {
        const children = collapsed ? [] : (item.children ?? []).filter(visible);
        const expanded = open === item.to;
        return (
          <li key={item.to}>
            <div className="flex items-center">
              <NavLink
                to={item.to}
                end={item.to === '/'}
                onClick={onNavigate}
                // The label is gone in the rail, so the name has to reach a
                // screen reader and a hovering mouse some other way.
                title={collapsed ? item.label : undefined}
                aria-label={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `flex flex-1 items-center rounded-xl text-sm font-medium transition ${
                    collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
                  } ${
                    isActive
                      ? 'bg-navy text-white'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                  }`
                }
              >
                <item.Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                {!collapsed && item.label}
              </NavLink>
              {!collapsed && children.length > 0 && (
                <button
                  className="ml-1 grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : item.to)}
                >
                  <ChevronDown
                    aria-hidden="true"
                    className={`h-4 w-4 transition ${expanded ? 'rotate-180' : ''}`}
                  />
                </button>
              )}
            </div>
            {children.length > 0 && expanded && (
              <ul className="ml-4 mt-1 space-y-1 border-l border-slate-200 pl-3 dark:border-slate-800">
                {children.map((child) => (
                  <li key={child.to}>
                    <NavLink
                      to={child.to}
                      // A child that shares its parent's path ("Suppliers" under Suppliers)
                      // must not stay lit on its siblings' pages.
                      end={child.to === item.to}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        `block rounded-lg px-3 py-1.5 text-sm transition ${
                          isActive
                            ? 'bg-brand-50 font-semibold text-brand-700 dark:bg-slate-800 dark:text-brand-100'
                            : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`
                      }
                    >
                      {child.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { user, signOut } = useAuth();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const updateReady = useUpdateReady();
  const { canInstall, install } = useInstallPrompt();

  const onSignOut = async (): Promise<void> => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-slate-50 pb-safe dark:bg-slate-950">
      <aside
        className={`fixed inset-y-0 left-0 hidden flex-col border-r border-slate-200 bg-white py-5 transition-[width] duration-200 dark:border-slate-800 dark:bg-slate-900 md:flex ${
          collapsed ? 'w-16 px-2' : 'w-64 px-4'
        }`}
      >
        <div className={`mb-6 flex items-center ${collapsed ? 'justify-center' : 'justify-between px-1'}`}>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-lg font-bold text-navy dark:text-white">ITCORE SF</p>
              <p className="truncate text-xs text-slate-500">ITCORE Solutions Inc.</p>
            </div>
          )}
          <button
            type="button"
            onClick={toggleCollapsed}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            aria-label={collapsed ? 'Expand the menu' : 'Collapse the menu'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand the menu' : 'Collapse the menu'}
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden="true" className="h-5 w-5" />
            ) : (
              <PanelLeftClose aria-hidden="true" className="h-5 w-5" />
            )}
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto" aria-label="Main">
          <NavTree collapsed={collapsed} />
        </nav>
        <div className="mt-4 border-t border-slate-200 pt-4 dark:border-slate-800">
          {!collapsed && (
            <>
              <p className="truncate px-3 text-sm font-medium text-slate-700 dark:text-slate-200">
                {user?.name}
              </p>
              <p className="truncate px-3 text-xs text-slate-500">{user?.roleName}</p>
            </>
          )}
          <button
            onClick={() => void onSignOut()}
            title={collapsed ? `Sign out — ${user?.name ?? ''}` : undefined}
            aria-label={collapsed ? 'Sign out' : undefined}
            className={`mt-2 flex w-full items-center rounded-xl text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 ${
              collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'
            }`}
          >
            <LogOut aria-hidden="true" className="h-4 w-4 shrink-0" />
            {!collapsed && 'Sign out'}
          </button>
        </div>
      </aside>

      <div
        className={`flex min-w-0 flex-1 flex-col transition-[margin] duration-200 ${
          collapsed ? 'md:ml-16' : 'md:ml-64'
        }`}
      >
        {updateReady && (
          <div className="flex flex-wrap items-center justify-between gap-2 bg-brand px-4 py-2 text-sm text-white">
            <span>A newer version of ITCORE SF is ready.</span>
            <button
              type="button"
              onClick={() => void applyUpdate()}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-white/15 px-3 font-semibold hover:bg-white/25"
            >
              <RefreshCw aria-hidden="true" className="h-4 w-4" /> Reload now
            </button>
          </div>
        )}

        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 pt-safe backdrop-blur dark:border-slate-800 dark:bg-slate-900/90">
          <button
            onClick={() => setMenuOpen((open) => !open)}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 md:hidden"
            aria-label="Menu"
            aria-expanded={menuOpen}
          >
            <Menu aria-hidden="true" className="h-5 w-5" />
          </button>
          <span className="font-semibold text-navy dark:text-white md:hidden">ITCORE SF</span>
          <div className="flex-1" />
          {canInstall && (
            <button
              onClick={() => void install()}
              className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              aria-label="Install ITCORE SF on this device"
              title="Install on this device"
            >
              <Download aria-hidden="true" className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={toggle}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {resolved === 'dark' ? (
              <Sun aria-hidden="true" className="h-4 w-4" />
            ) : (
              <Moon aria-hidden="true" className="h-4 w-4" />
            )}
          </button>
          <Button variant="ghost" size="sm" onClick={() => void onSignOut()} className="hidden md:inline-flex">
            <LogOut aria-hidden="true" className="h-4 w-4" /> Sign out
          </Button>
        </header>

        {menuOpen && (
          <div className="border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900 md:hidden">
            <NavTree onNavigate={() => setMenuOpen(false)} />
          </div>
        )}

        <main className="flex-1 px-4 py-6 md:px-6">{children}</main>
      </div>
    </div>
  );
}
